import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography, Button, Chip } from '@mui/material';
import { 
  Save as SaveIcon, 
  Visibility as ViewIcon, 
  ArrowBack as ArrowBackIcon 
} from '@mui/icons-material';
import { useSpreadsheetStore } from './store/spreadsheetStore';
import SpreadsheetBuilder from './SpreadsheetBuilder';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { message } from 'antd';

const SpreadsheetEdit = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { setSheets, setTableTitle, setTableDescription, sheets, tableTitle, tableDescription } = useSpreadsheetStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  // API bağlantısı test fonksiyonu
  const testAPIConnection = async () => {
    try {
      console.log('API bağlantısı test ediliyor...');
      const response = await axios.get(`/api/form/Libraries/list/0/1`, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
        },
        timeout: 10000
      });
      console.log('API bağlantısı başarılı:', response.status);
      return true;
    } catch (error) {
      console.error('API bağlantısı başarısız:', error);
      return false;
    }
  };

  // Tabloyu kaydetme fonksiyonu
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Önce API bağlantısını test et
      const connectionOK = await testAPIConnection();
      if (!connectionOK) {
        message.error('API bağlantısı kurulamadı. Lütfen internet bağlantınızı kontrol edin.');
        return;
      }
      
      // Store'dan güncel veriyi al
      const currentSheets = useSpreadsheetStore.getState().sheets;
      const currentTableTitle = useSpreadsheetStore.getState().tableTitle;
      const currentTableDescription = useSpreadsheetStore.getState().tableDescription;
      
      // formContent objesini oluştur
      const formContent = {
        id: formId,
        title: currentTableTitle || tableData?.title || 'Tablo',
        description: currentTableDescription || tableData?.description || '',
        sheets: currentSheets,
        settings: tableData?.settings || {
          theme: 'default',
          gridLines: true,
          headers: true,
          autoSave: true
        },
        isPublished: true,
        publishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        formLink: ''
      };

      // PUT isteği için veri yapısı - API'nin beklediği format
      const updateData = {
        parentId: "0",
        formId: formId,
        title: currentTableTitle || tableData?.title || 'Tablo',
        description: currentTableDescription || tableData?.description || '',
        formContent: formContent, // Obje olarak gönder
        isActive: true,
        fileType: 1 // Tablo tipi
      };

      console.log('SpreadsheetEdit: Kaydetme verisi:', updateData);
      console.log('SpreadsheetEdit: Request headers:', {
        ApplicationToken: appToken ? 'Var' : 'Yok',
        User: userData.id || 'Yok',
        'Content-Type': 'application/json'
      });
      console.log('SpreadsheetEdit: PUT URL:', `/api/form/Libraries/${formId}`);

      // PUT isteği gönder
      const response = await axios.put(
        `/api/form/Libraries`,
        updateData,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json'
          },
          transformRequest: [(data) => {
            console.log('PUT Transform request data:', typeof data, data);
            return JSON.stringify(data);
          }],
          timeout: 30000, // 30 saniye timeout ekle
          validateStatus: function (status) {
            return status < 500; // 500'den küçük tüm statusları kabul et
          }
        }
      );

      console.log('SpreadsheetEdit: Kaydetme response:', response.data);
      
      // Response durumunu kontrol et
      if (response.status >= 200 && response.status < 300) {
        message.success('Tablo başarıyla kaydedildi');
      } else {
        console.warn('SpreadsheetEdit: Beklenmeyen response status:', response.status);
        message.warning(`Tablo kaydedildi ancak beklenmeyen response: ${response.status}`);
      }
      
    } catch (error) {
      console.error('SpreadsheetEdit: Kaydetme hatası:', error);
      
      // Hata türünü belirle
      if (error.code === 'ERR_NETWORK') {
        console.error('Network Error Details:', {
          message: error.message,
          code: error.code,
          config: error.config,
          request: error.request?.readyState,
          status: error.request?.status,
          statusText: error.request?.statusText,
          responseURL: error.request?.responseURL
        });
        message.error('Ağ bağlantısı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
      } else if (error.response) {
        // Sunucu yanıtı var ama hata kodu
        console.error('Server Error:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
        message.error(`Sunucu hatası: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        // İstek yapıldı ama yanıt alınamadı
        console.error('Request Error:', {
          readyState: error.request.readyState,
          status: error.request.status,
          statusText: error.request.statusText,
          responseURL: error.request.responseURL
        });
        message.error('Sunucuya ulaşılamadı. Lütfen daha sonra tekrar deneyin.');
      } else {
        // İstek yapılırken hata oluştu
        console.error('Setup Error:', error.message);
        message.error('Tablo kaydedilirken bir hata oluştu: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('SpreadsheetEdit: API çağrısı başlıyor, formId:', formId);
        
        // Önce Tables.jsx'te kullanılan endpoint'i dene
        let response;
        try {
          response = await axios.get(`/api/form/Libraries/${formId}`, {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
            },
          });
          console.log('SpreadsheetEdit: Birincil API response:', response.data);
          
          // Response yapısını kontrol et ve düzelt
          if (response.data && response.data.formLibraries && Array.isArray(response.data.formLibraries)) {
            // formLibraries array'i döndü, hedef tabloyu bul
            const targetTable = response.data.formLibraries.find(table => 
              table.formId === formId || table.id === parseInt(formId)
            );
            
            if (!targetTable) {
              setError('Tablo bulunamadı');
              return;
            }
            
            response.data = targetTable;
            console.log('SpreadsheetEdit: Birincil API den formLibraries array içinden tablo bulundu:', response.data);
          }
          // Eğer response.data direkt tablo objesi ise, değişiklik yapmaya gerek yok
          
        } catch (firstError) {
          console.log('SpreadsheetEdit: Birincil endpoint başarısız, alternatif deneniyor...');
          // Alternatif endpoint dene
          response = await axios.get(`/api/form/Libraries/list/0/1`, {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
            },
          });
          
          // Belirli formId'yi bul
          const allTables = response.data.formLibraries || [];
          const targetTable = allTables.find(table => table.formId === formId || table.id === parseInt(formId));
          
          if (!targetTable) {
            setError('Tablo bulunamadı');
            return;
          }
          
          response.data = targetTable;
          console.log('SpreadsheetEdit: Alternatif API den tablo bulundu:', response.data);
        }

        // API yanıtını kontrol et
        const tableLibrary = response.data;
        
        if (!tableLibrary) {
          setError('Tablo bulunamadı');
          return;
        }

        console.log('SpreadsheetEdit: tableLibrary:', tableLibrary);
        console.log('SpreadsheetEdit: formContent type:', typeof tableLibrary.formContent);
        console.log('SpreadsheetEdit: formContent raw:', tableLibrary.formContent);
        
        // formContent kontrolü - boş veya null ise varsayılan yapı oluştur
        let parsedContent;
        
        if (!tableLibrary.formContent) {
          console.log('SpreadsheetEdit: formContent boş, varsayılan yapı oluşturuluyor');
          parsedContent = {
            id: formId,
            title: tableLibrary.title || 'Yeni Tablo',
            description: tableLibrary.description || '',
            sheets: [{
              name: 'Sayfa 1',
              id: 'sheet1',
              grid: Array(50).fill().map(() => Array(26).fill().map(() => ({
                value: '',
                formula: '',
                format: {
                  bold: false,
                  italic: false,
                  underline: false,
                  fontSize: 11,
                  fontFamily: 'Arial',
                  textColor: '#000000',
                  backgroundColor: '#ffffff',
                  horizontalAlign: 'left',
                  verticalAlign: 'top',
                  border: {
                    top: { style: 'none', color: '#000000' },
                    right: { style: 'none', color: '#000000' },
                    bottom: { style: 'none', color: '#000000' },
                    left: { style: 'none', color: '#000000' }
                  }
                }
              }))),
              activeCell: { row: 0, col: 0 },
              selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
              viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
              columnWidths: {},
              rowHeights: {}
            }],
            settings: {
              theme: 'default',
              gridLines: true,
              headers: true,
              autoSave: true
            }
          };
        } else {
          // formContent'i parse et - çift escape edilmiş olabilir
          try {
            let contentToParse = tableLibrary.formContent;
            
            // Eğer string ise ve çift escape edilmişse
            if (typeof contentToParse === 'string') {
              // İlk parse - çift escape durumu için
              try {
                const firstParse = JSON.parse(contentToParse);
                if (typeof firstParse === 'string') {
                  // İkinci parse - asıl content
                  parsedContent = JSON.parse(firstParse);
                } else {
                  parsedContent = firstParse;
                }
              } catch {
                // Tek parse yeterli
                parsedContent = JSON.parse(contentToParse);
              }
            } else {
              parsedContent = contentToParse;
            }
          } catch (parseError) {
            console.error('SpreadsheetEdit: formContent parse hatası:', parseError);
            console.log('SpreadsheetEdit: Parse hatası, varsayılan yapı oluşturuluyor');
            
            // Parse hatasında varsayılan yapı oluştur
            parsedContent = {
              id: formId,
              title: tableLibrary.title || 'Tablo',
              description: tableLibrary.description || '',
              sheets: [{
                name: 'Sayfa 1',
                id: 'sheet1',
                grid: Array(50).fill().map(() => Array(26).fill().map(() => ({
                  value: '',
                  formula: '',
                  format: {
                    bold: false,
                    italic: false,
                    underline: false,
                    fontSize: 11,
                    fontFamily: 'Arial',
                    textColor: '#000000',
                    backgroundColor: '#ffffff',
                    horizontalAlign: 'left',
                    verticalAlign: 'top',
                    border: {
                      top: { style: 'none', color: '#000000' },
                      right: { style: 'none', color: '#000000' },
                      bottom: { style: 'none', color: '#000000' },
                      left: { style: 'none', color: '#000000' }
                    }
                  }
                }))),
                activeCell: { row: 0, col: 0 },
                selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
                viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
                columnWidths: {},
                rowHeights: {}
              }],
              settings: {
                theme: 'default',
                gridLines: true,
                headers: true,
                autoSave: true
              }
            };
          }
        }

        console.log('SpreadsheetEdit: parsedContent:', parsedContent);

        // Sheets kontrolü ve varsayılan oluşturma
        if (!parsedContent.sheets || !Array.isArray(parsedContent.sheets) || parsedContent.sheets.length === 0) {
          console.log('SpreadsheetEdit: Geçerli sheets bulunamadı, varsayılan oluşturuluyor');
          parsedContent.sheets = [{
            name: 'Sayfa 1',
            id: 'sheet1',
            grid: Array(50).fill().map(() => Array(26).fill().map(() => ({
              value: '',
              formula: '',
              format: {
                bold: false,
                italic: false,
                underline: false,
                fontSize: 11,
                fontFamily: 'Arial',
                textColor: '#000000',
                backgroundColor: '#ffffff',
                horizontalAlign: 'left',
                verticalAlign: 'top',
                border: {
                  top: { style: 'none', color: '#000000' },
                  right: { style: 'none', color: '#000000' },
                  bottom: { style: 'none', color: '#000000' },
                  left: { style: 'none', color: '#000000' }
                }
              }
            }))),
            activeCell: { row: 0, col: 0 },
            selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
            viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
            columnWidths: {},
            rowHeights: {}
          }];
        } else {
          // Mevcut sheets'e eksik özellikleri ekle
          parsedContent.sheets = parsedContent.sheets.map((sheet, index) => ({
            ...sheet,
            activeCell: sheet.activeCell || { row: 0, col: 0 },
            selection: sheet.selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
            viewport: sheet.viewport || { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
            columnWidths: sheet.columnWidths || {},
            rowHeights: sheet.rowHeights || {}
          }));
        }

        // Store'a veriyi yükle
        setSheets(parsedContent.sheets);
        setTableTitle(parsedContent.title || tableLibrary.title);
        setTableDescription(parsedContent.description || tableLibrary.description);
        
        setTableData({
          title: parsedContent.title || tableLibrary.title,
          description: parsedContent.description || tableLibrary.description,
          formContent: parsedContent, // SpreadsheetBuilder için parsedContent'i ekle
          sheets: parsedContent.sheets,
          settings: parsedContent.settings || {},
          originalData: tableLibrary // Orijinal veriyi de sakla
        });
        
        console.log('SpreadsheetEdit: Store güncellendi, sheets sayısı:', parsedContent.sheets.length);
        
      } catch (err) {
        console.error('SpreadsheetEdit: Hata:', err);
        setError('Tablo yüklenirken bir hata oluştu: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (formId && appToken && userData?.id) {
      fetchData();
    } else {
      console.log('SpreadsheetEdit: Gerekli parametreler eksik:', { formId, appToken: !!appToken, userId: userData?.id });
      setLoading(false);
    }
  }, [formId, appToken, userData.id, setSheets, setTableTitle, setTableDescription]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          FormId: {formId}
        </Typography>
        <Button onClick={() => navigate('/tables')} sx={{ mt: 2 }} variant="contained">
          Tablolara Dön
        </Button>
        <Button onClick={() => window.location.reload()} sx={{ mt: 2, ml: 2 }} variant="outlined">
          Tekrar Dene
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Üst Başlık ve Aksiyonlar */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tables')}
              variant="outlined"
              size="small"
            >
              Tablolara Dön
            </Button>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {tableData?.title || 'Tablo Düzenleme'}
            </Typography>
            <Chip label="Düzenleme Modu" color="warning" size="small" />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              color="primary"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => navigate(`/spreadsheet/${formId}`)}
            >
              Görüntüle
            </Button>
          </Box>
        </Box>
        
        {tableData?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {tableData.description}
          </Typography>
        )}
      </Paper>

      {/* Spreadsheet - Edit Mode */}
      <Box sx={{ p: 0 }}>
        <SpreadsheetBuilder 
          readOnly={false} 
          formId={formId}
          onSave={handleSave}
          initialData={tableData}
        />
      </Box>
    </Box>
  );
};
export default SpreadsheetEdit;



